/**
 * Removes app-owned, user-scoped data from localStorage and sessionStorage.
 * Called on logout so a subsequent login on the same browser does not inherit
 * the previous user's cached state (Dashboard "Resume Watching", vocab
 * suggestion caches, last-worked segments, vocabulary filters, etc.).
 *
 * Supabase clears its own sb-* auth token keys during signOut(). This helper
 * only removes keys written by application code.
 *
 * Deliberately PRESERVED (global preferences, not user data):
 *   themeMode, i18nextLng, scribelate_transcript_font_size,
 *   scribelate_story_font_size, scribelate_story_playback_speed,
 *   transcript-font-size
 *
 * Note: vocabulary-selected-language IS cleared — it is a per-user filter
 * (the languages a user is studying), not a UI preference like i18nextLng.
 * Leaving it caused the next user's vocabulary list to load filtered by the
 * previous user's language.
 *
 * When adding a new user-scoped storage key anywhere in the app, register it
 * in one of the lists below.
 */

const LOCAL_EXACT = [
  'scribelate-last-yt-video',
  'scribelate-lt-buffer',
  'segment_celebration_count',
  'transcript_celebration_count',
  'translation_celebration_count',
  'loop_celebration_count',
  'vocabulary-selected-language',
];

const LOCAL_PREFIXES = [
  'scribelate-last-worked-segment-',
  'scribelate-last-worked-podcast-segment-',
  'vocab_suggestions_',
];

const SESSION_EXACT = [
  'vocabulary-filter-project',
  'vocabulary-filter-podcast',
  'vocabulary-filter-episode',
  'vocabulary-filter-search',
  'scribelate_onboarding_session_dismissed',
  'scribelate_first_episode_session_dismissed',
  'scribelate_podcast_discovery_session_dismissed',
];

const SESSION_PREFIXES: string[] = [];

function sweep(
  store: Storage,
  exact: string[],
  prefixes: string[],
): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key === null) continue;
      if (exact.includes(key) || prefixes.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      store.removeItem(key);
    }
  } catch {
    // Storage unavailable (private browsing, disabled) — nothing to clear.
  }
}

export function clearUserScopedStorage(): void {
  sweep(localStorage, LOCAL_EXACT, LOCAL_PREFIXES);
  sweep(sessionStorage, SESSION_EXACT, SESSION_PREFIXES);
}
